import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sparkles, Globe, Mic, Video, Star, Heart, HeartOff } from 'lucide-react';

const tierColors = {
  standard: 'from-cyan-400 to-blue-500',
  premium: 'from-purple-400 to-pink-500',
  luxury: 'from-amber-400 to-orange-500',
  custom: 'from-emerald-400 to-teal-500'
};

const tierBadgeColors = {
  standard: 'bg-cyan-100 text-cyan-700',
  premium: 'bg-purple-100 text-purple-700',
  luxury: 'bg-amber-100 text-amber-700',
  custom: 'bg-emerald-100 text-emerald-700'
};

const categoryLabels = {
  brand_ambassador: 'Brand Ambassador',
  host: 'Event Host',
  influencer: 'Digital Influencer',
  presenter: 'Corporate Presenter',
  mc: 'Master of Ceremonies'
};

const capabilityIcons = {
  multilingual: Globe,
  scripted_delivery: Mic,
  virtual_presence: Video,
  live_interaction: Sparkles
};

const AmbassadorCard = ({ 
  ambassador, 
  isFavorited = false, 
  onToggleFavorite,
  showFavoriteButton = true 
}) => {
  const navigate = useNavigate();
  const gradientClass = tierColors[ambassador.tier] || tierColors.standard;
  const badgeClass = tierBadgeColors[ambassador.tier] || tierBadgeColors.standard;

  const handleCardClick = () => {
    navigate(`/ambassador/${ambassador.slug}`);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(ambassador.id, !isFavorited);
    }
  };

  return (
    <Card 
      className="group relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
      onClick={handleCardClick}
      data-testid="ambassador-card"
    >
      {/* Gradient Border Effect */}
      <div className={`absolute inset-0 bg-gradient-to-r ${gradientClass} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} style={{ padding: '2px' }}>
        <div className="absolute inset-[2px] bg-white rounded-lg" />
      </div>
      
      <CardContent className="relative p-0">
        {/* Avatar Section */}
        <div className="relative">
          <div className={`h-48 bg-gradient-to-br ${gradientClass} flex items-center justify-center`}>
            <img
              src={ambassador.avatar_url}
              alt={ambassador.name}
              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(ambassador.name)}&size=128&background=6366f1&color=fff`;
              }}
            />
          </div>
          
          {/* AI Ambassador Badge */}
          <Badge 
            className="absolute top-3 left-3 bg-white/90 text-slate-800 font-semibold flex items-center gap-1 shadow-sm"
            data-testid="ai-ambassador-badge"
          >
            <Sparkles className="w-3 h-3 text-purple-500" />
            AI Ambassador
          </Badge>
          
          {/* Tier Badge */}
          {ambassador.tier !== 'standard' && (
            <Badge className={`absolute top-3 right-3 ${badgeClass} capitalize`}>
              {ambassador.tier}
            </Badge>
          )}
          
          {/* Favorite Button */}
          {showFavoriteButton && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute bottom-3 right-3 bg-white/80 hover:bg-white rounded-full shadow-sm"
              onClick={handleFavoriteClick}
              data-testid="favorite-btn"
            >
              {isFavorited ? (
                <Heart className="w-5 h-5 fill-pink-500 text-pink-500" />
              ) : (
                <Heart className="w-5 h-5 text-gray-500" />
              )}
            </Button>
          )}
        </div>
        
        {/* Info Section */}
        <div className="p-4 space-y-3">
          {/* Name & Category */}
          <div>
            <h3 className="text-lg font-bold text-slate-900" data-testid="ambassador-name">
              {ambassador.name}
            </h3>
            <p className="text-sm text-muted-foreground">
              {categoryLabels[ambassador.category] || ambassador.category}
            </p>
          </div>
          
          {/* Tagline */}
          <p className="text-sm font-medium text-slate-600 line-clamp-1" data-testid="ambassador-tagline">
            {ambassador.tagline}
          </p>
          
          {/* Capabilities */}
          <div className="flex flex-wrap gap-1">
            {ambassador.capabilities?.slice(0, 3).map((cap) => {
              const Icon = capabilityIcons[cap] || Sparkles;
              return (
                <Badge 
                  key={cap} 
                  variant="outline" 
                  className="text-xs flex items-center gap-1 bg-slate-50"
                >
                  <Icon className="w-3 h-3" />
                  {cap.replace(/_/g, ' ')}
                </Badge>
              );
            })}
            {ambassador.capabilities?.length > 3 && (
              <Badge variant="outline" className="text-xs bg-slate-50">
                +{ambassador.capabilities.length - 3}
              </Badge>
            )}
          </div>
          
          {/* Languages */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Globe className="w-3 h-3" />
            <span>{ambassador.supported_languages?.slice(0, 3).join(', ')}</span>
            {ambassador.supported_languages?.length > 3 && (
              <span>+{ambassador.supported_languages.length - 3}</span>
            )}
          </div>
          
          {/* Price */}
          <div className="pt-2 border-t flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900" data-testid="ambassador-price">
              {ambassador.price_display}
            </span>
            <Button 
              size="sm" 
              className={`bg-gradient-to-r ${gradientClass} text-white hover:opacity-90`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/ambassador/${ambassador.slug}`);
              }}
            >
              View Profile
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AmbassadorCard;
